import type { PaymentMethod } from "@stripe/stripe-js";
import { z } from "zod";

import { onboardingUserSchema } from "@/features/onboarding/models";
import type {
  CreateHealthieUserParams,
  OnboardingUser,
} from "@/features/onboarding/models";
import type { UrlParamsSlice } from "@/features/onboarding/stores";
import type { CreditAmount } from "@/features/referral/models";
import type { Coupon, Price } from "@/shared/domains";
import {
  Subscription,
  SUBSCRIPTION_VENDOR,
  SubscriptionWithBillingDetails,
} from "@/shared/domains";
import { parseResponseData, privateAPI, publicAPI } from "@/shared/lib";
import type { ServiceResponse } from "@/shared/lib/axios";
import type { CancellationReason } from "../models";
import { cancellationDiscountSchema } from "../models";

export const acceptRequestedPlanChange = async (accepted: boolean) => {
  const { data } = await privateAPI.put<ServiceResponse<null>>(
    "/patients/subscriptions/requested-plan-change",
    { accepted },
  );

  return data.data;
};

interface ChangeNextPeriodSubscriptionParams {
  subscriptionId: Subscription["id"];
  productPriceId: Price["id"];
}

export const changeNextPeriodSubscription = async ({
  subscriptionId,
  productPriceId,
}: ChangeNextPeriodSubscriptionParams) => {
  const { data } = await privateAPI.put<ServiceResponse<null>>(
    `/patients/subscriptions/${subscriptionId}/schedule`,
    { nextProductPriceId: productPriceId },
  );

  return data.data;
};

/** A dynamic intake form's answers, by field id (the questionnaire funnel). */
interface IntakeFormAnswersPayload {
  formId: string;
  answers: {
    fieldId: string;
    value: string;
  }[];
}

/**
 * Answers to the funnel's backend intake screens, keyed by question key. The
 * backend maps them to the intake form of the subscription's vendor.
 */
interface IntakeFormFunnelAnswersPayload {
  funnelAnswers: Record<string, string | string[]>;
}

export type IntakeFormPayload =
  | IntakeFormAnswersPayload
  | IntakeFormFunnelAnswersPayload;

interface CreateSubscriptionsAndPatientParams extends CreateHealthieUserParams {
  userCode: OnboardingUser["code"];
  subscriptions: Price["id"][];
  paymentMethodId: PaymentMethod["id"];
  couponCode?: Coupon["code"];
  metadata?: UrlParamsSlice["urlParams"];
  isCallCenter: boolean;
  intakeForm?: IntakeFormPayload;
}

const createSubscriptionsAndPatientResponseSchema = z.object({
  token: onboardingUserSchema.shape.token.nonoptional(),
  paymentIntentIds: z.string().array(),
  invoiceIds: z.string().array(),
  needsSync: z.boolean().optional(),
  vendor: z.enum(SUBSCRIPTION_VENDOR).nullish(),
});

export const createSubscriptionsAndPatient = async ({
  userCode,
  ...params
}: CreateSubscriptionsAndPatientParams) => {
  const response = await publicAPI.post(
    `/patients/${userCode}/create-subscriptions-and-patient`,
    params,
  );

  return parseResponseData(
    response,
    createSubscriptionsAndPatientResponseSchema,
  );
};

export const getPatientCurrentSubscriptions = async () => {
  const response = await privateAPI.get("/patients/subscriptions");

  return parseResponseData(response, SubscriptionWithBillingDetails.array());
};

interface CreatePatientSubscriptionsParams {
  subscriptions: Price["id"][];
  couponCode?: Coupon["code"];
  referralCredit?: CreditAmount;
}

export const createPatientSubscriptions = async (
  params: CreatePatientSubscriptionsParams,
) => {
  const response = await privateAPI.post("/patients/subscriptions", params);

  return parseResponseData(response, Subscription.array());
};

export const getCancellationDiscount = async (
  subscriptionId: Subscription["id"],
) => {
  const { data } = await privateAPI.get<ServiceResponse<unknown>>(
    `patients/subscriptions/${subscriptionId}/cancellation-discount-available`,
  );

  return cancellationDiscountSchema.parse(data.data);
};

interface ApplyCancellationDiscountParams {
  subscriptionId: Subscription["id"];
  cancellationReasons: CancellationReason["id"][];
}

export const applyCancellationDiscount = async ({
  subscriptionId,
  cancellationReasons,
}: ApplyCancellationDiscountParams) => {
  const { data } = await privateAPI.put<ServiceResponse<null>>(
    `patients/subscriptions/${subscriptionId}/accept-cancellation-discount`,
    { cancellationReasons },
  );

  return data.data;
};

export const cancelSubscription = async ({
  subscriptionId,
  cancellationReasons,
}: ApplyCancellationDiscountParams) => {
  const { data } = await privateAPI.put<ServiceResponse<null>>(
    `patients/subscriptions/${subscriptionId}/cancel-date`,
    { cancellationReasons },
  );

  return data.data;
};

export const sendCancellationRequest = async (
  subscriptionId: Subscription["id"],
) => {
  return await privateAPI.post(
    `patients/subscriptions/${subscriptionId}/cancellation-request`,
  );
};
